import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'

import { useRouter } from 'next/router'

import styles from '@shared/components/articleDetail/style.module.scss'
import noImage from '@assets/images/placeholder/article-placeholder.jpg'
import { getImgURL, abbreviateNumber, checkPageNumberInSlug } from '@shared-submodule/utils'
import { BLUR_DATA_URL_BASE64, DEFAULT_BLOG_READ, WIDGET } from '@shared-submodule/sm-constants'
import Layout from '../layout'
import { GET_FANTASY_ARTICLE_VIEWS } from '@graphql/article/article.query'
import { useLazyQuery } from '@apollo/client'
import { allRoutes } from '@shared-submodule/sm-constants/allRoutes'
import MyImage from '@shared/components/myImage'
import AllWidget from '@shared/components/allWidget'
import CustomLink from '../customLink'
import BreadcrumbNav from '@shared/components/breadcrumbNav'
import ArticleActions from '@shared/components/articleDetail/articleActions'
import CurrentSeries from '../widgets/currentSeries'
import { reviveAdData } from '@shared/components/reviveAds/reviveAdData'

const ReviveAds = dynamic(() => import('@shared/components/reviveAds'), { ssr: false })
const ExclusiveIcon = dynamic(() => import('@shared/components/ctIcons/exclusiveIcon'), { ssr: false })
const EditorsPickIcon = dynamic(() => import('@shared/components/ctIcons/editorsPickIcon'), { ssr: false })
const LazyLoad = dynamic(() => import('@shared/components/lazyLoad'), { ssr: false })
// const DownloadApp = dynamic(() => import('@shared/components/downloadApp'))
// const Comments = dynamic(() => import('@shared/components/comments'))
// const OnMouseAndScroll = dynamic(() => import('@shared/components/lazyLoad/onMouseAndScroll'))
const ATSAds = dynamic(() => import('@shared/components/atsAds'), { ssr: false })

function FantasyArticleDetail({ article, isPreviewMode, seoData, children, type, currentSeriesData, isMobile = false }) {
  const router = useRouter()

  const [articleViews, setArticleViews] = useState(article?.nViewCount || article?.nOViews || 0)
  const [page, setPage] = useState(1)
  const [commentCount] = useState(article?.nCommentCount || 0)

  const [getFantasyArticleViews] = useLazyQuery(GET_FANTASY_ARTICLE_VIEWS, {
    onCompleted: data => {
      if (data && data?.getFrontFantasyArticle) {
        setArticleViews(data?.getFrontFantasyArticle?.nViewCount || data?.getFrontFantasyArticle?.nOViews || 0)
      }
    }
  })

  useEffect(() => {
    const slug = router?.asPath?.split('/').filter(e => e)
    const { lastSlug } = checkPageNumberInSlug(slug)
    setPage(lastSlug ? Number(lastSlug) || 1 : 1)
  }, [router?.asPath])

  useEffect(() => {
    getFantasyArticleViews({ variables: { input: { _id: article?._id } } })
  }, [])

  const { addLeft, addTop, mobSm, addBottom, addRight } = reviveAdData

  return (
    <Layout data={{ ...article, oSeo: { ...article?.oSeo, ...seoData } }} isPreviewMode={isPreviewMode}>
      <main className={`${styles.articlePage}`}>
        <section className={`common-section ${type === 'fantasyArticle' ? 'pt-3' : 'pt-4'}`}>
          <ATSAds />
          <div className="container">
            <div className="row justify-content-center justify-content-lg-start">
              <div className={`col-lg-9 ${isPreviewMode ? '' : 'left-content'}`}>
                <div className='d-flex justify-content-center d-md-none'>
                  <ReviveAds {...mobSm} className={' mb-2'} />
                </div>
                <div className='d-none d-md-flex justify-content-center'>
                  <ReviveAds {...addTop} className={' mb-2'} />
                </div>
                <section className={`${styles.articleBlock} pb-2 mb-2 position-relative`}>
                  <div
                    className={`d-flex align-items-start justify-content-between ${
                      article?.oAdvanceFeature?.bEditorsPick && article?.oAdvanceFeature?.bExclusiveArticle && 'flex-column flex-sm-row'
                    }`}>
                    {!isPreviewMode && <BreadcrumbNav isArticle listicleCurrentPage={page} />}
                    <div className="d-flex mb-2 mb-md-0">
                      {article?.oAdvanceFeature?.bEditorsPick && (
                        <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize`}>
                          <EditorsPickIcon />
                          {"Editor's Pick"}
                        </span>
                      )}
                      {article?.oAdvanceFeature?.bExclusiveArticle && (
                        <span className={`badge bg-light" ${styles.advFeature} d-flex align-items-center text-capitalize ms-2`}>
                          <ExclusiveIcon />
                          {'Exclusive Article'}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* <article className="article-details-page"> */}
                  <article>
                    <h1 className="mb-1">{article?.sTitle}</h1>
                    {!isMobile &&
                    <div className={`${styles.authorBlock} d-flex flex-wrap align-items-center mb-3 mb-lg-4`}>
                      <div className={`${styles.author} d-flex align-items-center big-text font-semi text-capitalize`}>
                        <span>
                          {article?.oDisplayAuthor?.oSeo?.sSlug ? (
                            <CustomLink href={allRoutes.authorDetail(article?.oDisplayAuthor?.oSeo?.sSlug)} prefetch={false}>
                              <a>{article?.oDisplayAuthor?.sDisplayName}</a>
                            </CustomLink>
                          ) : (
                            article?.oDisplayAuthor?.sDisplayName
                          )}
                          {article?.oDisplayAuthor?.bIsVerified && (
                            <span className={`${styles.verfied} d-inline-block rounded-circle ms-1`}></span>
                          )}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className={`${styles.views} d-flex align-items-center mb-0 text-capitalize`}>
                          {'views'} : {abbreviateNumber(articleViews)}
                        </p>
                        <p className={`${styles.views} ${styles.duration} d-flex align-items-center mb-0`}>
                          {article?.nDuration > 0 ? article?.nDuration : DEFAULT_BLOG_READ} {'Min'} {'Read'}
                        </p>
                      </div>
                      {/* <p className={`${styles.dates} w-100 xsmall-text font-semi mt-2 mb-0`}>
                        {(article?.dPublishDisplayDate || article?.dPublishDate) && (
                          <>
                            <time
                              className="op-published isDesk"
                              dateTime={dateCheck(article?.dPublishDisplayDate || article?.dPublishDate)?.toISOString()}>
                              {"Published"} -{' '}
                              {convertDt24hFormat(dateCheck(article?.dPublishDisplayDate || article?.dPublishDate))}
                            </time>
                          </>
                        )}
                      </p> */}
                    </div>
}
                    {/* <h2 className={`${styles.subTitle} small-head pt-2 pt-sm-3 pt-lg-4`}>{article?.sSubtitle}</h2> */}
                    {/* <span className={`${styles.subTitle} small-head text-secondary text-wrap`}>TOKEN: {token}</span> */}
                    {page <= 1 && (
                      <figure className="op-tracker">
                        <div className={`${styles.fantasyPostImg} position-relative overflow-hidden`}>
                          <MyImage
                            priority={true}
                            src={getImgURL(article?.oImg?.sUrl) || noImage}
                            alt={article?.oImg?.sText || article?.sSrtTitle}
                            placeholder="blur"
                            blurDataURL={BLUR_DATA_URL_BASE64}
                            height="505"
                            width="960"
                            layout="responsive"
                            sizes="(max-width: 767px) 320px, (max-width: 991px) 360px, (max-width: 1190px) 400px, 600px"
                          />
                          {/* {article?.oImg?.sCaption !== 'null' && (
                            <figcaption
                              className={`${styles.caption} px-3`}
                              dangerouslySetInnerHTML={{ __html: article?.oImg?.sCaption }}
                            />
                          )} */}
                        </div>
                      </figure>
                    )}
                    <div className={`${styles.articleActionsMob} text-center`}>
                      {!isPreviewMode && <ArticleActions commentCount={commentCount} article={article} type={type} seoData={seoData} />}
                    </div>
                    {children}
                  </article>
                  {/* {article?.oAdvanceFeature?.bAllowComments && !isMobile && (
                    <OnMouseAndScroll>
                      <Comments
                        type={type}
                        commentCount={commentCount}
                        setCommentCount={setCommentCount}
                        articleId={article?._id}
                        count={article?.nCommentCount}
                        isRequireAdminApproval={article?.oAdvanceFeature?.bRequireAdminApproval}
                      />
                    </OnMouseAndScroll>
                  )} */}
                  {/* <OnMouseAndScroll>
                    <DownloadApp isArticle />
                  </OnMouseAndScroll> */}
                </section>
                <div className='d-flex justify-content-center d-md-none'>
                  <ReviveAds {...addLeft} className={' mb-2'} />
                </div>
                <div className='d-md-flex justify-content-center d-none'>
                  <ReviveAds {...addBottom} className={' mb-2'} />
                </div>
                {/* Desktop/Mobile above related Video and article  */}
                {/* <div ref={observe}>
                  {(!isPreviewMode && inView) && (
                    <> */}
                {/* {(width < 768) && <PixFuture />} */}
                {/* {(width > 768 && (article?.oCategory?._id || article?.iCategoryId)) && (
                        <ArticleRelatedVideos categoryId={article?.oCategory?._id || article?.iCategoryId} articleId={article?._id} type={article?.oSeo?.eType} />
                      )} */}
                {/* <ErrorBoundary>
                        <TaboolaAds />
                      </ErrorBoundary> */}
                {/* </>
                  )}
                </div> */}
              </div>
              <div className="col-lg-3 common-sidebar isDesk">
                {!isPreviewMode && (
                  <>
                    <CurrentSeries dataSSR={currentSeriesData} />
                    <ATSAds />
                    <ReviveAds {...addLeft} className={'d-flex justify-content-center mb-2'} />
                    <LazyLoad>
                      <AllWidget type={WIDGET.trendingNews} show />
                    </LazyLoad>
                    <ReviveAds {...addRight} className={'d-flex justify-content-center sticky-ads'} />
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

FantasyArticleDetail.propTypes = {
  article: PropTypes.object,
  seoData: PropTypes.object,
  isPreviewMode: PropTypes.bool,
  children: PropTypes.node.isRequired,
  type: PropTypes.string,
  currentSeriesData: PropTypes.object,
  isMobile: PropTypes.bool
}

export default FantasyArticleDetail
